import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../components/AppShell';
import { staffOverviewApi, lakh } from '../lib/staffOverviewApi';
import type { StaffOverview, StaffRow } from '../lib/staffOverviewApi';
import { StatTile, ChartFrame, HBarChart } from '../components/charts';

/**
 * THE WHOLE STAFF ON ONE SCREEN — what they cost, whether they turned up, and what they are owed.
 *
 * ⚠ THE FINANCIAL YEAR IS APRIL TO MARCH. "Last year's salary" to an Indian school means the last
 * FY, because that is what the auditor, the TDS return and the Form 16 all use. A calendar year
 * here would produce a figure matching nothing on any document the school files.
 *
 * ⚠ SALARY COLUMNS ARE ABSENT, NOT BLANK, FOR ANYONE OUTSIDE `tenant-admin`. Payroll is admin-only
 * in this product — Program.cs says so explicitly about the hr policy — so a Principal sees every
 * attendance figure here and no money at all. The page detects that and drops the columns rather
 * than rendering a row of dashes that looks like missing data.
 */
export function StaffOverviewPage() {
  const { user } = useAuth();
  const [d, setD] = useState<StaffOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [q, setQ] = useState('');
  const [only, setOnly] = useState<'current' | 'away' | 'left'>('current');

  // ⚠ Mirrors the backend `tenant-admin` policy. The catch below covers it drifting.
  const canSeePay = !!user && (ROLES.admin as readonly string[]).includes(user.role);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    staffOverviewApi
      .load(canSeePay)
      .catch(() => (canSeePay ? staffOverviewApi.load(false) : Promise.reject(new Error('no'))))
      .then((r) => {
        if (!alive) return;
        setD(r);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [canSeePay]);

  if (loading && !d) return <p style={{ color: 'var(--ink-3)' }}>Adding up the staff room…</p>;
  if (failed || !d) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the staff overview.</div>;
  }

  const t = d.totals;
  const pay = d.includesPay;
  const today = new Date(d.asOf + 'T00:00:00');

  const shown = d.staff.filter((s) => {
    if (only === 'left' && !s.exitDate) return false;
    if (only === 'current' && s.exitDate) return false;
    if (only === 'away' && !(s.onLeaveToday || s.todayStatus === 'Absent')) return false;
    if (!q) return true;
    const hay = `${s.fullName} ${s.code} ${s.designation ?? ''} ${s.department ?? ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Staff overview</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '76ch' }}>
          Everyone on the roll, with attendance for today, this month, {d.financialYear} and{' '}
          {d.previousFinancialYear}
          {pay ? ', and what each of them has been paid.' : '.'}{' '}
          Financial years run April to March.
        </p>
      </div>

      {!pay && (
        <div className="card" style={{
          padding: '10px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6, color: 'var(--ink-2)',
        }}>
          ⚠ Salary figures are not shown to your role. Payroll is restricted to Trust and Campus
          Admin in this product — that is an existing decision in the access policy, not a fault on
          this screen.
        </div>
      )}

      {d.note && (
        <div className="card" style={{
          padding: '11px 14px', marginBottom: 14, fontSize: 12.5, lineHeight: 1.6,
          color: 'var(--ink-2)', background: 'var(--surface-2)',
        }}>⚠ {d.note}</div>
      )}

      <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <div className="staff-tiles" style={{ marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="On the roll" value={String(t.headcount)}
              foot={`${t.teaching} teaching · ${t.support} support`} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            {/* ⚠ "—" not "0" when the register was never opened. Zero people in is a catastrophe;
                nobody having marked the register is a Tuesday, and a Sunday is neither. */}
            <StatTile label="In today"
              value={t.presentToday + t.absentToday + t.onLeaveToday === 0 ? '—' : String(t.presentToday)}
              foot={t.presentToday + t.absentToday + t.onLeaveToday === 0
                ? 'no register marked for today'
                : `${t.onLeaveToday} on leave · ${t.absentToday} absent${
                    t.unmarkedToday > 0 ? ` · ${t.unmarkedToday} not marked` : ''}`} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Attendance this month"
              /* ⚠ "—" when nothing was marked. 0% says nobody came in. */
              value={t.attendancePctThisMonth === null ? '—' : `${t.attendancePctThisMonth.toFixed(1)}%`}
              foot="approved leave excluded from the denominator" />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <StatTile label="Average tenure" value={`${t.averageTenureYears.toFixed(1)} yrs`}
              foot={`${t.joinedThisYear} joined · ${t.leftThisYear} left in ${d.financialYear}`} />
          </div>
          {pay && (
            <>
              <div className="card" style={{ padding: 16 }}>
                <StatTile label="Monthly payroll" value={lakh(t.monthlyGrossPaise)}
                  foot={`${lakh(t.monthlyNetPaise)} net · ${lakh(t.employerContributionsPaise)} employer share`} />
              </div>
              <div className="card" style={{ padding: 16 }}>
                <StatTile label={`Paid ${d.financialYear}`} value={lakh(t.paidThisFyPaise)}
                  foot={`${lakh(t.paidLastFyPaise)} in ${d.previousFinancialYear}`} />
              </div>
            </>
          )}
        </div>

        {pay && d.months.length > 1 && (
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            {/* ⚠ PAISE TO LAKH IS ÷10,000,000, NOT ÷100,000. One lakh rupees is 100,000 rupees,
                which is ten million paise. The first cut divided by 100,000 and the chart read
                "₹3440.5 L" for a ₹34 lakh payroll — a hundredfold error sitting right beside a
                tile showing the correct figure. Caught by looking at the render, not the code.
                ⚠ Also note a JSX comment cannot live in an attribute list; it belongs here. */}
            <ChartFrame
              title="Payroll, month by month"
              subtitle={`Net paid across ${d.previousFinancialYear} and ${d.financialYear}. Gross minus statutory and voluntary deductions — the employer's own PF and ESI share is not in this line, because it does not come out of anybody's salary.`}
            >
              <HBarChart
                categories={d.months.map((m) => m.month)}
                series={[{ name: 'Net paid', color: 'var(--s1)', values: d.months.map((m) => m.netPaise / 1e7) }]}
                format={(n) => `₹${n.toFixed(2)} L`}
                labelWidth={78}
                valueWidth={78}
              />
            </ChartFrame>
          </div>
        )}

        <div className="card" style={{ padding: 16 }}>
          <div className="viz-title" style={{ marginBottom: 4 }}>Everyone</div>
          <p className="viz-sub">
            &ldquo;Teaching&rdquo; is worked out from the designation and from who runs a class — designations
            are free text, so it is close rather than exact.
          </p>

          <div className="tab-strip staff-controls" style={{ borderBottom: 'none', marginBottom: 10, gap: 6 }}>
            {([['current', `On the roll (${d.staff.filter((s) => !s.exitDate).length})`],
               ['away', 'Away today'],
               ['left', `Left (${d.staff.filter((s) => s.exitDate).length})`]] as Array<[string, string]>)
              .map(([k, label]) => (
                <button key={k} type="button"
                        className={'chip' + (only === k ? ' is-on' : '')}
                        onClick={() => setOnly(k as 'current' | 'away' | 'left')}>
                  {label}
                </button>
              ))}
            <input className="input" style={{ width: 'auto', minWidth: 170 }}
                   placeholder="Search name, code, department"
                   value={q} onChange={(e) => setQ(e.target.value)} />
          </div>

          {shown.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: 0 }}>Nobody matches that.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="viz-table" style={{ minWidth: pay ? 980 : 720 }}>
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Today</th>
                    <th className="num">Month</th>
                    <th className="num">{d.financialYear}</th>
                    <th className="num">{d.previousFinancialYear}</th>
                    <th className="num">Leave</th>
                    {pay && <th className="num">Gross / mo</th>}
                    {pay && <th className="num">Paid {d.financialYear}</th>}
                    {pay && <th className="num">Paid {d.previousFinancialYear}</th>}
                    <th className="num">Tenure</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => <Row key={s.employeeId} s={s} pay={pay} today={today} />)}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
            ⚠ Attendance percentages leave approved leave and holidays out of the denominator — a
            teacher on sanctioned leave has not failed to attend. A half day counts as half.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ s, pay, today }: { s: StaffRow; pay: boolean; today: Date }) {
  const gone = !!s.exitDate && new Date(s.exitDate + 'T00:00:00') <= today;
  const badge =
    s.onLeaveToday ? 'pill-warn'
      : s.todayStatus === 'Absent' ? 'pill-danger'
      : s.todayStatus === 'Present' ? 'pill-ok'
      : 'pill-muted';

  return (
    <tr style={{ opacity: gone ? 0.55 : 1 }}>
      <td>
        {s.fullName}
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginLeft: 6 }}>{s.code}</span>
        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)' }}>
          {s.designation ?? '—'}{s.department ? ` · ${s.department}` : ''}
          {s.classesOwned.length > 0 && ` · class teacher, ${s.classesOwned.join(', ')}`}
        </span>
        {gone && (
          <span className="pill pill-muted" style={{ fontSize: 10 }}>left {s.exitDate}</span>
        )}
      </td>
      <td>
        <span className={'pill ' + badge} style={{ fontSize: 10 }}>
          {s.onLeaveToday ? 'On leave' : s.todayStatus}
        </span>
      </td>
      <td className="num">{s.thisMonthPct === null ? '—' : `${s.thisMonthPct.toFixed(0)}%`}</td>
      <td className="num">{s.thisYearPct === null ? '—' : `${s.thisYearPct.toFixed(0)}%`}</td>
      <td className="num" style={{ color: 'var(--ink-3)' }}>
        {s.lastYearPct === null ? '—' : `${s.lastYearPct.toFixed(0)}%`}
      </td>
      <td className="num">
        {s.leaveDaysThisYear}
        <span style={{ fontSize: 10.5, color: 'var(--ink-3)' }}> / {s.leaveBalanceDays} left</span>
      </td>
      {pay && <td className="num">{lakh(s.monthlyGrossPaise)}</td>}
      {pay && <td className="num">{lakh(s.paidThisFyPaise)}</td>}
      {pay && <td className="num" style={{ color: 'var(--ink-3)' }}>{lakh(s.paidLastFyPaise)}</td>}
      <td className="num">{s.tenureYears.toFixed(1)}</td>
    </tr>
  );
}
